import { Component, OnInit, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ReportQueueService } from '@lmc-app/core/services/report-queue.service';
import { CellValueChangedEvent, SideBarDef } from 'ag-grid-community';
import { SnackbarConstants } from '@lmc-app/shared/constants/snackbar.constant';
import { ButtonCellRendererComponent } from '@lmc-app/custom-table/button-cell-renderer/button-cell-renderer.component';
import { ReadReportModalComponent } from './read-report-modal/read-report-modal.component';
import { Loading } from '../../applications/loading';

@Component({
	selector: 'app-report-queue',
	templateUrl: './report-queue.component.html',
	styleUrls: ['./report-queue.component.scss'],
})
export class ReportQueueComponent implements OnInit {
	protected sideBar: SideBarDef | string | string[] | boolean | null;
	protected columnDefs = [
		{
			headerName: 'Date',
			field: 'schedule_date',
			sortable: true,
			filter: 'agTextColumnFilter',
		},
		{
			headerName: 'Time',
			field: 'schedule_time',
			sortable: true,
			filter: 'agTextColumnFilter',
			editable: true,
			valueSetter: params => {
				if (params.data.status === "Generated") {
					alert("Generated report cannot be updated");
					return false; // Date of past report cannot be updated
				}
				const regex = /^([01]\d|2[0-3]):([0-5]\d)$/;
				if (!regex.test(params.newValue)) {
					alert("Time format must be HH:mm");
					return false; // ❌ value NOT accepted
				}
				params.data.schedule_time = params.newValue;
				return true; // ✔ value accepted
			}
		},
		{
			headerName: 'Name',
			valueGetter: (params) => params.data.reportType?.name,
		},
		{
			headerName: 'Status',
			field: 'status',
			sortable: true,
		},
		{
			headerName: 'Details',
			cellRenderer: ButtonCellRendererComponent,
			cellRendererParams: {
				label: 'Chat',
				matIconLabel: 'chat',
				clicked: (params: any) => {
					this.dialog.open(ReadReportModalComponent, {
						height: 'fit-content',
						width: 'fit-content',
						autoFocus: true,
						data: params,
					});
				},
			}
		},
	];
	protected reportQueue = [];
	protected isLoading: boolean = false;

	private readonly _bottomSheet = inject(MatBottomSheet);
	private readonly _snackBar = inject(MatSnackBar);

	constructor(
		private reportQueueService: ReportQueueService,
		private dialog: MatDialog,
	) { }

	ngOnInit(): void {
		this.isLoading = true;
		this.getReportQueue();
		this.setReportQueue();
	}

	onCellValueChanged(e: CellValueChangedEvent): void {
		this._bottomSheet.open(Loading, {
			disableClose: true,
			data: { message: 'Saving' },
		});
		this.reportQueueService.updateReport(e.data).subscribe({
			next: (res) => {
				this.getReportQueue();
				this._snackBar.open(
					`Rescheduled to ${res.schedule_date}@${res.schedule_time}.`,
					SnackbarConstants.ACTION_DISMISS,
					SnackbarConstants.DURATION_DISMISS_SHORT
				);
				this._bottomSheet.dismiss();
			},
			error: (error) => {
				this.getReportQueue();
				this._bottomSheet.dismiss();
				console.error(error);
				this._snackBar.open(
					(error.error && (error.error.message && (error.error.message.name || error.error.message))) || 'Error updating loan account.',
					SnackbarConstants.ACTION_DISMISS,
					SnackbarConstants.DURATION_DISMISS_SHORT
				);
			},
		});
	}

	getReportQueue(): void {
		this.reportQueueService.getReportQueue().subscribe({
			next: (res) => {
				this.reportQueue = res;
				this.isLoading = false;
			},
			error: (err) => {
				console.error(err);
				this.isLoading = false;
			},
		});
	}

	setReportQueue(): void {
		this.sideBar = {
			toolPanels: [
				{
					id: 'filters',
					labelDefault: 'Filters',
					labelKey: 'filters',
					iconKey: 'filter',
					toolPanel: 'agFiltersToolPanel',
				},
				{
					id: 'columns',
					labelDefault: 'Columns',
					labelKey: 'columns',
					iconKey: 'columns',
					toolPanel: 'agColumnsToolPanel',
					toolPanelParams: {
						suppressPivotMode: true,
						suppressValues: true,
						suppressRowGroups: true,
					},
				},
			],
			defaultToolPanel: 'customStats',
		};
	}
}
